import { createContext, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'

import { createFormatters, createTranslator } from './translator'
import type {
  Formatters,
  Locale,
  Messages,
  Namespace,
  TranslateFunction,
  TranslatorMode,
} from './types'

// Bewust géén 'use client' in dit pakket: de consument bepaalt waar de grens
// tussen server en client ligt. Next.js-apps zetten de directive in hun eigen
// wrapper; de Vite-SPA heeft hem niet nodig.

/** Waarde die de provider beschikbaar stelt. */
export interface I18nContextValue {
  readonly locale: Locale
  readonly messages: Messages
  readonly t: TranslateFunction
  readonly formatters: Formatters
}

const I18nContext = createContext<I18nContextValue | null>(null)

/** Props van `I18nProvider`. */
export interface I18nProviderProps {
  readonly locale: Locale
  readonly messages: Messages
  /** Alleen voor tests; normaal wordt de modus uit `NODE_ENV` afgeleid. */
  readonly mode?: TranslatorMode
  readonly children?: ReactNode
}

/**
 * Stelt locale, berichten, translator en formatteerfuncties beschikbaar aan de
 * componentboom. De translator en de formatters worden gememoïseerd, zodat een
 * hernieuwde render geen nieuwe Intl-instanties oplevert.
 */
export function I18nProvider({ locale, messages, mode, children }: I18nProviderProps): ReactNode {
  const value = useMemo<I18nContextValue>(() => {
    // `mode` alleen meegeven wanneer die gezet is: met exactOptionalPropertyTypes
    // is een expliciete `undefined` geen geldige waarde voor een optioneel veld.
    const t = createTranslator(
      mode === undefined ? { locale, messages } : { locale, messages, mode },
    )
    return { locale, messages, t, formatters: createFormatters(locale) }
  }, [locale, messages, mode])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

/** Leest de context of werpt wanneer de provider ontbreekt. */
export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext)
  if (context === null) {
    throw new Error('useI18n moet binnen een <I18nProvider> gebruikt worden')
  }
  return context
}

/**
 * Vertaalfunctie, optioneel vastgezet op één namespace.
 *
 * `useTranslations('guest')` levert een `t` waaraan sleutels zonder het
 * namespace-voorvoegsel worden meegegeven: `t('hero.title')`.
 */
export function useTranslations(namespace?: Namespace): TranslateFunction {
  const { t } = useI18n()
  return useMemo<TranslateFunction>(() => {
    if (namespace === undefined) {
      return t
    }
    return (key, values) => t(`${namespace}.${key}`, values)
  }, [t, namespace])
}

/** De actieve locale. */
export function useLocale(): Locale {
  return useI18n().locale
}

/** Datum-, getal- en valutaformattering voor de actieve locale. */
export function useFormatters(): Formatters {
  return useI18n().formatters
}
